import { useEffect } from 'react';

interface SEOStructuredDataProps {
  type?: 'website' | 'article' | 'person' | 'course';
  title?: string;
  description?: string;
  url?: string;
  image?: string;
  author?: string;
  datePublished?: string;
  dateModified?: string;
  category?: string;
}

export default function SEOStructuredData({
  type = 'website',
  title = "Aram AI - Documenting the AI Solopreneur Journey",
  description = "Follow Aram AI's journey as an AI solopreneur. Learn about AI tools, Armenian AI community, productivity tips, and entrepreneurship insights.",
  url = "https://tezzarida.io",
  image = "https://tezzarida.io/social-preview.png",
  author = "Aram AI",
  datePublished,
  dateModified,
  category
}: SEOStructuredDataProps) {
  useEffect(() => {
    // Remove existing structured data
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    let structuredData: any = {
      "@context": "https://schema.org",
      "@type": type === 'website' ? "WebSite" : type === 'article' ? "Article" : type === 'person' ? "Person" : "Course",
      "name": title,
      "description": description,
      "url": url,
      "image": image
    };

    if (type === 'website') {
      structuredData = {
        ...structuredData,
        "author": {
          "@type": "Person",
          "name": author,
          "url": "https://tezzarida.io/bio"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Aram AI",
          "logo": {
            "@type": "ImageObject",
            "url": "https://tezzarida.io/social-preview.png"
          }
        },
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://tezzarida.io/?search={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      };
    }

    if (type === 'article') {
      structuredData = {
        ...structuredData,
        "headline": title,
        "author": {
          "@type": "Person",
          "name": author
        },
        "publisher": {
          "@type": "Organization",
          "name": "Aram AI",
          "logo": {
            "@type": "ImageObject",
            "url": "https://tezzarida.io/social-preview.png"
          }
        },
        "datePublished": datePublished,
        "dateModified": dateModified || datePublished,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": url
        }
      };

      if (category) {
        structuredData.articleSection = category;
      }
    }

    if (type === 'person') {
      structuredData = {
        ...structuredData,
        "@type": "Person",
        "name": "Aram AI",
        "jobTitle": "AI Solopreneur",
        "description": "AI solopreneur documenting the journey of building AI-powered businesses and sharing insights about artificial intelligence, entrepreneurship, and the Armenian tech community.",
        "url": "https://tezzarida.io",
        "image": "https://tezzarida.io/social-preview.png",
        "sameAs": [
          "https://www.instagram.com/aram.ai",
          "https://www.linkedin.com/in/aramb",
          "https://www.youtube.com/@aramai"
        ],
        "knowsAbout": [
          "Artificial Intelligence",
          "Machine Learning", 
          "Entrepreneurship",
          "Software Development",
          "Armenian Technology"
        ]
      };
    }

    if (type === 'course') {
      structuredData = {
        ...structuredData,
        "@type": "Course",
        "provider": {
          "@type": "Organization",
          "name": "Aram AI"
        },
        "hasCourseInstance": {
          "@type": "CourseInstance",
          "courseMode": "online",
          "instructor": {
            "@type": "Person",
            "name": author
          }
        }
      };
    }

    // Create and append script tag
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    // Cleanup
    return () => {
      const scriptToRemove = document.querySelector('script[type="application/ld+json"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [type, title, description, url, image, author, datePublished, dateModified, category]);

  return null;
}